// src/components/admin/mail/mailUtils.ts
import { useEffect, useState } from 'react';
import { ComposeMode, MailAddress, MailMessageDetail } from '../../../types/mail';

/**
 * Per-account accent colours. Repeated in four places — the active pill, the workspace
 * accent bar, the reply modal header and the modal's left border — so "which mailbox am I
 * in" is answered peripherally rather than only by reading.
 */
export interface AccountAccent {
    gradient: string;
    solid: string;
    soft: string;
}

export const ACCOUNT_ACCENTS: { [key: string]: AccountAccent } = {
    work: {
        gradient: 'linear-gradient(135deg, #990147 0%, #cc3366 100%)',
        solid: '#990147',
        soft: 'rgba(153, 1, 71, 0.25)',
    },
    gmail: {
        gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        solid: '#1e3a8a',
        soft: 'rgba(30, 58, 138, 0.25)',
    },
};

const FALLBACK_ACCENT = ACCOUNT_ACCENTS.work;

export const accentFor = (accountId: string): AccountAccent =>
    ACCOUNT_ACCENTS[accountId] || FALLBACK_ACCENT;

/** Stable React key. Never combine uid and uidValidity arithmetically — that overflows. */
export const messageKey = (accountId: string, uidValidity: number, uid: number): string =>
    accountId + ':' + uidValidity + ':' + uid;

/**
 * `toLocaleDateString('sk-SK')` renders "3. 8. 2026" for everything, which is unreadable in
 * a list. Show the most useful precision for how recent the message is.
 */
export const formatMailDate = (iso: string | null): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const sameDay =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    if (sameDay) {
        return date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
    }

    const daysAgo = (now.getTime() - date.getTime()) / 86400000;
    if (daysAgo >= 0 && daysAgo < 7) {
        return date.toLocaleDateString('sk-SK', { weekday: 'long' });
    }
    if (date.getFullYear() === now.getFullYear()) {
        return date.getDate() + '. ' + (date.getMonth() + 1) + '.';
    }
    return date.toLocaleDateString('sk-SK');
};

export const formatFullDate = (iso: string | null): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    return (
        date.toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric', year: 'numeric' }) +
        ' o ' +
        date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
    );
};

export const formatBytes = (bytes: number): string => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' kB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const formatAddress = (address: MailAddress | null | undefined): string => {
    if (!address) return '';
    return address.name && address.name.trim() ? address.name : address.address;
};

export const formatAddressList = (addresses: MailAddress[] | null | undefined): string => {
    if (!addresses || addresses.length === 0) return '';
    return addresses.map(formatAddress).join(', ');
};

/** First letter for the avatar circle in a list row. */
export const initialOf = (address: MailAddress | null | undefined): string => {
    const label = formatAddress(address);
    return label ? label.charAt(0).toUpperCase() : '?';
};

const stripPrefixes = (subject: string | null): string =>
    (subject || '').replace(/^(\s*(re|odp|fwd|fw)\s*(\[\d+])?\s*:\s*)+/i, '').trim();

/** Strips every leading Re:/RE:/Re[2]:/Odp:/Fwd: run, then adds exactly one "Re: ". */
export const buildReplySubject = (subject: string | null): string => {
    const base = stripPrefixes(subject);
    return base ? 'Re: ' + base : 'Re:';
};

/**
 * "Fw: " rather than the Slovak "Preposlané:" — non-Slovak clients recognise Fw/Fwd and
 * thread accordingly, and a localised prefix silently breaks that.
 */
export const buildForwardSubject = (subject: string | null): string => {
    const base = stripPrefixes(subject);
    return base ? 'Fw: ' + base : 'Fw:';
};

/** Formats one address the way it should appear in a To/Cc field. */
export const addressToInput = (address: MailAddress): string => address.address;

/** Splits the comma-separated contents of a To/Cc/Bcc field. */
export const parseAddressList = (value: string): string[] =>
    value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

/**
 * Removes the account's own address from a recipient list.
 *
 * Without this, reply-all mails her own mailbox on every single reply — the classic bug,
 * and a confusing one for a non-technical user who then sees her own message arrive.
 */
export const stripSelfAddresses = (addresses: MailAddress[], ownAddress: string): MailAddress[] => {
    const own = ownAddress.trim().toLowerCase();
    const seen: { [key: string]: boolean } = {};
    const result: MailAddress[] = [];

    addresses.forEach((a) => {
        const key = (a.address || '').trim().toLowerCase();
        if (!key || key === own || seen[key]) return;
        seen[key] = true;
        result.push(a);
    });
    return result;
};

/** Header block prepended to a forwarded message body. */
export const buildForwardBody = (detail: MailMessageDetail): string => {
    const source = detail.bodyText && detail.bodyText.trim()
        ? detail.bodyText
        : htmlToPlainText(detail.bodyHtml);

    const lines = [
        '',
        '',
        '---------- Preposlaná správa ----------',
        'Od: ' + formatAddress(detail.from) + ' <' + (detail.from ? detail.from.address : '') + '>',
        'Dátum: ' + formatFullDate(detail.sentAt),
        'Predmet: ' + detail.subject,
        'Komu: ' + formatAddressList(detail.to),
        '',
        source,
        '',
    ];
    return lines.join('\n');
};

/**
 * Rough HTML → text, only ever used to build the quoted original in the composer.
 * Quoted HTML is never re-rendered into the reply.
 */
export const htmlToPlainText = (html: string | null): string => {
    if (!html) return '';
    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

/** "Dňa 3. 8. 2026 o 14:32 Jana Nováková <jana@example.sk> napísal(a):" + "> " prefixes. */
export const buildQuotedBody = (detail: MailMessageDetail): string => {
    const source = detail.bodyText && detail.bodyText.trim()
        ? detail.bodyText
        : htmlToPlainText(detail.bodyHtml);

    const author = detail.from
        ? (detail.from.name ? detail.from.name + ' <' + detail.from.address + '>' : detail.from.address)
        : '';

    const header = 'Dňa ' + formatFullDate(detail.sentAt) + ' ' + author + ' napísal(a):';
    const quoted = source
        .split('\n')
        .map((line) => '> ' + line)
        .join('\n');

    return '\n\n' + header + '\n' + quoted + '\n';
};

/** Slovak title for the compose modal, by mode. */
export const composeTitle = (mode: ComposeMode): string => {
    switch (mode) {
        case 'reply':
            return 'Odpoveď';
        case 'replyAll':
            return 'Odpoveď všetkým';
        case 'forward':
            return 'Preposlať správu';
        case 'new':
        default:
            return 'Nová správa';
    }
};

/** 12-line matchMedia hook — the project has no media-query utility of its own. */
export const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState<boolean>(
        () => typeof window !== 'undefined' && window.matchMedia(query).matches
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const list = window.matchMedia(query);
        const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
        setMatches(list.matches);
        list.addEventListener('change', onChange);
        return () => list.removeEventListener('change', onChange);
    }, [query]);

    return matches;
};
